import { Controller } from "stimulus"

// #1859: Instellingen > Bedrijfsinstellingen > Huisstijl & Thema.
//
// Per kleur werken de kleurkiezer en het HEX-veld elkaar bij. Elke wijziging
// zet de --v-*-variabelen op het live voorbeeld; de opgeslagen huisstijl van
// het bedrijf verandert pas na Opslaan. Kleuren die "automatisch volgen"
// (bijvoorbeeld de primaire knop volgt de primaire kleur) rekenen mee zolang
// de gebruiker ze niet zelf aanpast. De rekenwijze is dezelfde als
// Huisstijl::Kleur en Huisstijl::Thema op de server.
export default class extends Controller {
  static targets = ["veld", "kiezer", "hex", "fout", "contrast", "voorbeeld", "preset", "formulier", "herstelFormulier"]
  static values = { presets: Object, afgeleid: Object, paren: Array, namen: Object, merkkleuren: Array, teksten: Object }

  connect() {
    this.beginwaarden = this.leesAlles()
    this.beginpreset = this.huidigePreset()
    this.beginVolgt = Object.fromEntries(this.veldTargets.map((veld) => [veld.dataset.sleutel, veld.dataset.volgt]))
    this.gewijzigd = false
    this.verlaten = (event) => {
      if (!this.gewijzigd) return
      event.preventDefault()
      event.returnValue = this.tekstenValue.verlaten
    }
    this.turboVerlaten = (event) => {
      if (this.gewijzigd && !window.confirm(this.tekstenValue.verlaten)) event.preventDefault()
    }
    window.addEventListener("beforeunload", this.verlaten)
    document.addEventListener("turbo:before-visit", this.turboVerlaten)
    this.ververs()
  }

  disconnect() {
    window.removeEventListener("beforeunload", this.verlaten)
    document.removeEventListener("turbo:before-visit", this.turboVerlaten)
  }

  // --- invoer -------------------------------------------------------------

  kiesKleur(event) {
    const sleutel = event.target.dataset.sleutel
    const hex = this.hexVoor(sleutel)
    hex.value = event.target.value.toUpperCase()
    this.zelfGekozen(sleutel)
    this.toonFout(sleutel, false)
    this.ververs()
  }

  typHex(event) {
    const sleutel = event.target.dataset.sleutel
    const kleur = this.normaliseer(event.target.value)
    this.toonFout(sleutel, event.target.value.trim() !== "" && !kleur)
    if (kleur) this.kiezerVoor(sleutel).value = kleur.toLowerCase()
    this.zelfGekozen(sleutel)
    this.ververs()
  }

  kiesPreset(event) {
    const nieuw = event.target.value
    if (nieuw === "aangepast") {
      this.laatstePreset = nieuw
      return this.ververs()
    }

    const basis = this.presetsValue[nieuw] || {}
    const vorige = this.presetsValue[this.laatstePreset || this.beginpreset] || {}
    const eigen = Object.keys(basis).some((sleutel) => {
      if (this.merkkleurenValue.includes(sleutel) || sleutel === "primary") return false
      const huidig = this.normaliseer(this.hexVoor(sleutel)?.value)
      return huidig && vorige[sleutel] && huidig !== vorige[sleutel]
    })
    if (eigen && !window.confirm(this.tekstenValue.preset)) {
      const terug = this.presetTargets.find((radio) => radio.value === (this.laatstePreset || this.beginpreset))
      if (terug) terug.checked = true
      return
    }
    Object.entries(basis).forEach(([sleutel, waarde]) => {
      if (this.merkkleurenValue.includes(sleutel) || sleutel === "primary") return
      const hex = this.hexVoor(sleutel)
      if (!hex) return
      hex.value = waarde
      this.kiezerVoor(sleutel).value = waarde.toLowerCase()
      this.toonFout(sleutel, false)
    })
    // afgeleide kleuren gaan weer automatisch mee
    Object.keys(this.afgeleidValue).forEach((sleutel) => {
      if (this.merkkleurenValue.includes(sleutel)) return
      const veld = this.veldVoor(sleutel)
      if (veld) veld.dataset.volgt = "true"
    })
    this.laatstePreset = nieuw
    this.ververs()
  }

  markeer() {
    this.anderGewijzigd = true
    this.zetGewijzigd(true)
  }

  // --- knoppen ------------------------------------------------------------

  verstuur(event) {
    const ongeldig = this.hexTargets.find((hex) => hex.value.trim() !== "" && !this.normaliseer(hex.value))
    if (ongeldig) {
      event.preventDefault()
      this.toonFout(ongeldig.dataset.sleutel, true)
      ongeldig.focus()
      return
    }
    this.gewijzigd = false
  }

  annuleer() {
    // na de reset van het formulier: terug naar de beginwaarden van de pagina
    setTimeout(() => {
      Object.entries(this.beginwaarden).forEach(([sleutel, waarde]) => {
        const hex = this.hexVoor(sleutel)
        hex.value = waarde
        const kleur = this.normaliseer(waarde)
        if (kleur) this.kiezerVoor(sleutel).value = kleur.toLowerCase()
        this.toonFout(sleutel, false)
        this.veldVoor(sleutel).dataset.volgt = this.beginVolgt[sleutel]
      })
      this.laatstePreset = null
      this.anderGewijzigd = false
      this.ververs()
      this.zetGewijzigd(false)
    }, 0)
  }

  bevestigHerstellen(event) {
    if (!window.confirm(this.tekstenValue.herstellen)) {
      event.preventDefault()
      return
    }
    this.gewijzigd = false
  }

  toonVoorbeeld(event) {
    event.preventDefault()
    const doel = document.getElementById("huisstijl-voorbeeld")
    if (!doel) return
    doel.scrollIntoView({ behavior: "smooth", block: "start" })
    doel.focus({ preventScroll: true })
  }

  // --- rekenen en tonen ---------------------------------------------------

  ververs() {
    const kleuren = this.leesGeldig()
    Object.entries(this.afgeleidValue).forEach(([sleutel, regel]) => {
      const veld = this.veldVoor(sleutel)
      if (!veld || veld.dataset.volgt !== "true") return
      const waarde = this.afleiden(regel, kleuren)
      if (!waarde) return
      kleuren[sleutel] = waarde
      this.hexVoor(sleutel).value = waarde
      this.kiezerVoor(sleutel).value = waarde.toLowerCase()
    })
    if (this.hasVoorbeeldTarget) {
      Object.entries(kleuren).forEach(([sleutel, waarde]) => this.voorbeeldTarget.style.setProperty(`--v-${sleutel}`, waarde))
    }
    this.controleerContrast(kleuren)
    this.zetGewijzigd(this.isGewijzigd())
  }

  afleiden([soort, bron, tweede, aandeel], kleuren) {
    const waarde = kleuren[bron]
    if (!waarde) return null
    if (soort === "kopie") return waarde
    if (soort === "tekst_op") return this.tekstOp(waarde)
    if (soort === "leesbaar") return kleuren[tweede] ? this.leesbaar(waarde, kleuren[tweede]) : waarde
    if (soort === "meng") return kleuren[tweede] ? this.meng(waarde, kleuren[tweede], aandeel) : waarde
    return null
  }

  controleerContrast(kleuren) {
    const meldingen = {}
    this.parenValue.forEach(([voor, op, grens]) => {
      if (!kleuren[voor] || !kleuren[op]) return
      const ratio = this.contrast(this.naarRgb(kleuren[voor]), this.naarRgb(kleuren[op]))
      if (ratio >= grens) return
      const tekst = this.tekstOp(kleuren[op])
      const tekstGoed = !["link", "nav_active", "success", "warning", "error", "info"].includes(voor) &&
        this.contrast(this.naarRgb(tekst), this.naarRgb(kleuren[op])) >= grens
      const voorstel = tekstGoed ? tekst : this.leesbaar(kleuren[voor], kleuren[op])
      meldingen[voor] = meldingen[voor] || { voor, op, ratio, grens, voorstel }
    })
    this.contrastTargets.forEach((blok) => {
      const melding = meldingen[blok.dataset.sleutel]
      blok.hidden = !melding
      if (!melding) return
      const tekst = this.tekstenValue.contrast
        .replace("%{voor}", this.namenValue[melding.voor])
        .replace("%{op}", this.namenValue[melding.op])
        .replace("%{ratio}", melding.ratio.toFixed(1))
        .replace("%{grens}", melding.grens)
      const span = blok.querySelector("span")
      span.textContent = `${tekst} `
      const knop = document.createElement("button")
      knop.type = "button"
      knop.className = "hs-voorstel"
      knop.textContent = this.tekstenValue.voorstel.replace("%{kleur}", melding.voorstel)
      knop.addEventListener("click", () => this.gebruikVoorstel(melding.voor, melding.voorstel))
      span.appendChild(knop)
    })
  }

  gebruikVoorstel(sleutel, kleur) {
    this.hexVoor(sleutel).value = kleur
    this.kiezerVoor(sleutel).value = kleur.toLowerCase()
    this.zelfGekozen(sleutel)
    this.ververs()
  }

  // --- hulpjes ------------------------------------------------------------

  leesAlles() {
    return Object.fromEntries(this.hexTargets.map((hex) => [hex.dataset.sleutel, hex.value]))
  }

  leesGeldig() {
    const kleuren = {}
    this.hexTargets.forEach((hex) => {
      const kleur = this.normaliseer(hex.value)
      if (kleur) kleuren[hex.dataset.sleutel] = kleur
    })
    return kleuren
  }

  isGewijzigd() {
    if (this.huidigePreset() !== this.beginpreset) return true
    const nu = this.leesAlles()
    return Object.keys(nu).some((sleutel) => (nu[sleutel] || "").toUpperCase() !== (this.beginwaarden[sleutel] || "").toUpperCase())
  }

  // kleuren en thema vergelijkt isGewijzigd; een logo of de schakelaar markeert zelf
  zetGewijzigd(waarde) {
    this.gewijzigd = waarde || this.anderGewijzigd === true
    this.element.dataset.gewijzigd = this.gewijzigd ? "true" : "false"
  }

  huidigePreset() {
    return this.presetTargets.find((radio) => radio.checked)?.value
  }

  zelfGekozen(sleutel) {
    const veld = this.veldVoor(sleutel)
    if (veld) veld.dataset.volgt = "false"
  }

  toonFout(sleutel, aan) {
    const blok = this.foutTargets.find((el) => el.dataset.sleutel === sleutel)
    if (blok) blok.hidden = !aan
    const hex = this.hexVoor(sleutel)
    if (hex) hex.setAttribute("aria-invalid", aan ? "true" : "false")
  }

  veldVoor(sleutel) { return this.veldTargets.find((el) => el.dataset.sleutel === sleutel) }
  hexVoor(sleutel) { return this.hexTargets.find((el) => el.dataset.sleutel === sleutel) }
  kiezerVoor(sleutel) { return this.kiezerTargets.find((el) => el.dataset.sleutel === sleutel) }

  normaliseer(waarde) {
    let w = (waarde || "").trim().replace(/^#/, "")
    if (!/^([0-9a-f]{3}|[0-9a-f]{6})$/i.test(w)) return null
    if (w.length === 3) w = w.split("").map((c) => c + c).join("")
    return `#${w.toUpperCase()}`
  }

  naarRgb(hex) {
    const w = this.normaliseer(hex).replace("#", "")
    return [0, 2, 4].map((i) => parseInt(w.substr(i, 2), 16))
  }

  naarHex(rgb) {
    return `#${rgb.map((c) => Math.max(0, Math.min(255, Math.round(c))).toString(16).padStart(2, "0")).join("").toUpperCase()}`
  }

  luminantie(rgb) {
    const [r, g, b] = rgb.map((c) => {
      const v = c / 255
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
    })
    return 0.2126 * r + 0.7152 * g + 0.0722 * b
  }

  contrast(een, twee) {
    const [licht, donker] = [this.luminantie(een), this.luminantie(twee)].sort((a, b) => b - a)
    return (licht + 0.05) / (donker + 0.05)
  }

  donker(hex) {
    const rgb = this.naarRgb(hex)
    return this.contrast(rgb, [255, 255, 255]) > this.contrast(rgb, [0, 0, 0])
  }

  tekstOp(hex) {
    const rgb = this.naarRgb(hex)
    return this.contrast(rgb, [21, 25, 28]) > this.contrast(rgb, [255, 255, 255]) ? "#15191C" : "#FFFFFF"
  }

  meng(kleur, met, aandeel) {
    const a = this.naarRgb(kleur)
    const b = this.naarRgb(met)
    return this.naarHex(a.map((x, i) => x * aandeel + b[i] * (1 - aandeel)))
  }

  leesbaar(kleur, achtergrond) {
    const bg = this.naarRgb(achtergrond)
    let [h, s, l] = this.naarHsl(this.naarRgb(kleur))
    let kandidaat = this.naarRgb(kleur)
    if (this.donker(achtergrond)) {
      while (this.contrast(kandidaat, bg) < 4.5 && l < 0.8) {
        l += 0.01
        kandidaat = this.hslNaarRgb(h, s, l)
      }
    } else {
      while (this.contrast(kandidaat, bg) < 4.5 && l > 0.1) {
        l -= 0.01
        kandidaat = this.hslNaarRgb(h, s, l)
      }
    }
    return this.naarHex(kandidaat)
  }

  naarHsl([rood, groen, blauw]) {
    const [r, g, b] = [rood, groen, blauw].map((c) => c / 255)
    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    const l = (max + min) / 2
    if (max === min) return [0, 0, l]
    const d = max - min
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    let h
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0)
    else if (max === g) h = (b - r) / d + 2
    else h = (r - g) / d + 4
    return [h / 6, s, l]
  }

  hslNaarRgb(h, s, l) {
    if (s === 0) return [Math.round(l * 255), Math.round(l * 255), Math.round(l * 255)]
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s
    const p = 2 * l - q
    return [h + 1 / 3, h, h - 1 / 3].map((t) => {
      if (t < 0) t += 1
      if (t > 1) t -= 1
      let waarde
      if (t < 1 / 6) waarde = p + (q - p) * 6 * t
      else if (t < 1 / 2) waarde = q
      else if (t < 2 / 3) waarde = p + (q - p) * (2 / 3 - t) * 6
      else waarde = p
      return Math.max(0, Math.min(255, Math.round(waarde * 255)))
    })
  }
}
